import {
  Users,
  Clock,
  CheckCircle,
  Ticket,
  UserCheck,
  UserX,
  XCircle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { useNavigate, useSearchParams } from 'react-router';
import { useAuth } from '@/providers/AuthProvider';
import { useMetrics } from '@/hooks/api/useMetrics';
import { cn } from '@/lib/utils';
import { MetricsSkeleton } from './MetricsSkeleton';
import { TrendBadge } from '@/components/TrendBadge';
import { Sparkline } from '@/components/Sparkline';
import { TicketPriority, TicketStatus } from '@packages/database/types';
import {
  MetricPeriod,
  MetricWithTrend,
  TimeSeriesPoint,
} from '@/api/metricService';
import { PeriodSelector } from '@/components/PeriodSelector';
// import type {
//   MetricWithTrend,
//   TimeSeriesPoint,
// } from '@packages/database/types';

// ── Navigation Helper ────────────────────────────────────

function useMetricNavigation() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = user?.role.toLowerCase();

  return (path: string, filters: Record<string, string>) => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value) params.set(key, value);
    }
    const query = params.toString();
    navigate(`/${role}/${path}${query ? `?${query}` : ''}`);
  };
}

// ── Metric Card ──────────────────────────────────────────

interface MetricCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  description?: string;
  filters?: Record<string, string>;
  trend?: MetricWithTrend;
  invertTrend?: boolean;
  sparkline?: TimeSeriesPoint[];
  sparkColor?: string;
}

function MetricCard({
  title,
  value,
  icon,
  description,
  filters,
  trend,
  invertTrend,
  sparkline,
  sparkColor,
}: MetricCardProps) {
  const navigateTo = useMetricNavigation();
  const isClickable = !!filters;

  return (
    <Card
      onClick={() => isClickable && navigateTo('ticket-map', filters!)}
      className={cn(
        'border-border shadow-sm bg-card transition-all overflow-hidden',
        isClickable && 'cursor-pointer hover:shadow-md hover:border-primary/30',
      )}
    >
      <CardContent className="pt-6 pb-2">
        <div className="flex items-center justify-between mb-2">
          <div className="text-muted-foreground">{icon}</div>
        </div>
        <div className="text-2xl font-semibold text-card-foreground mb-1">
          {value ?? 0}
        </div>
        <div className="text-sm font-medium text-muted-foreground">{title}</div>
        {trend && (
          <div className="mt-1">
            <TrendBadge metric={trend} invertColor={invertTrend} />
          </div>
        )}
        {description && !trend && (
          <div className="text-xs text-muted-foreground/70 mt-1">
            {description}
          </div>
        )}
      </CardContent>
      {sparkline && sparkline.length > 0 && (
        <div className="px-2 pb-1">
          <Sparkline
            data={sparkline}
            color={sparkColor || '#6366f1'}
            height={36}
          />
        </div>
      )}
    </Card>
  );
}

// ── Status Row ───────────────────────────────────────────

type StatusColor = 'amber' | 'blue' | 'indigo' | 'emerald' | 'rose';

const PROGRESS_COLORS: Record<StatusColor, string> = {
  amber: 'bg-amber-500 dark:bg-amber-600',
  blue: 'bg-blue-500 dark:bg-blue-600',
  indigo: 'bg-indigo-500 dark:bg-indigo-600',
  emerald: 'bg-emerald-500 dark:bg-emerald-600',
  rose: 'bg-rose-500 dark:bg-rose-600',
};

function StatusRow({
  status,
  count,
  total,
  color,
  filterValue,
}: {
  status: string;
  count: number;
  total: number;
  color: StatusColor;
  filterValue: TicketStatus;
}) {
  const navigateTo = useMetricNavigation();
  const safeTotal = total > 0 ? total : 1;
  const percentage = Math.min(Math.max((count / safeTotal) * 100, 0), 100);

  return (
    <div
      className="space-y-2 p-2 rounded-md cursor-pointer transition-colors hover:bg-muted/50"
      onClick={() => navigateTo('ticket-map', { status: filterValue })}
    >
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-foreground">{status}</span>
          <Badge
            variant="outline"
            className="text-[10px] px-1.5 h-5 min-w-[1.5rem] justify-center bg-background"
          >
            {count || 0}
          </Badge>
        </div>
        <span className="text-xs text-muted-foreground">
          {percentage.toFixed(0)}%
        </span>
      </div>
      <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
        <div
          className={cn(
            'h-2 rounded-full transition-all duration-500',
            PROGRESS_COLORS[color],
          )}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

// ── Priority Indicator ───────────────────────────────────

const PRIORITY_STYLES: Record<StatusColor, string> = {
  rose: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  amber: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  emerald:
    'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  indigo:
    'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
};

function PriorityIndicator({
  label,
  value,
  color,
  filterValue,
}: {
  label: string;
  value: number;
  color: StatusColor;
  filterValue: TicketPriority;
}) {
  const navigateTo = useMetricNavigation();

  return (
    <div
      className={cn(
        'rounded-lg p-4 text-center cursor-pointer transition-all hover:brightness-95 dark:hover:brightness-110',
        PRIORITY_STYLES[color],
      )}
      onClick={() => navigateTo('ticket-map', { priority: filterValue })}
    >
      <div className="text-2xl font-bold mb-1">{value || 0}</div>
      <div className="text-xs font-semibold uppercase tracking-wider opacity-80">
        {label}
      </div>
    </div>
  );
}

// ── Main Component ───────────────────────────────────────

export const MetricsOverview = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const period = (searchParams.get('period') as MetricPeriod) || '7d';
  const projectId = searchParams.get('projectId') || undefined;
  const { data: metrics, isLoading } = useMetrics(period, projectId);
  const navigateTo = useMetricNavigation();

  const ticketData = metrics?.ticketMetrics;
  const crewData = metrics?.crewMetrics;

  const handlePeriodChange = (newPeriod: MetricPeriod) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('period', newPeriod);
      return next;
    });
  };

  const trendLabel = {
    '1d': 'vs yesterday',
    '7d': 'vs prev 7 days',
    '30d': 'vs prev 30 days',
    '90d': 'vs prev 90 days',
  }[period];

  if (isLoading) return <MetricsSkeleton />;

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* <div className="flex items-center justify-between mb-4 shrink-0">
      <h2 className="text-lg font-semibold text-foreground">Overview</h2>
      <PeriodSelector value={period} onChange={handlePeriodChange} />
    </div> */}
      {/* ── TOP 4 METRIC CARDS ──────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 shrink-0">
        <MetricCard
          title="Open Tickets"
          value={ticketData?.totalOpen.current || 0}
          icon={<Ticket className="h-5 w-5" />}
          // trend={ticketData?.totalOpen}
          // invertTrend={true}
          // sparkline={ticketData?.sparklines.created}
          // sparkColor="#6366f1"
          filters={{ category: 'open' }}
        />

        <MetricCard
          title="Completed"
          value={ticketData?.completed.current || 0}
          icon={<CheckCircle className="h-5 w-5" />}
          // trend={ticketData?.completed}
          // sparkline={ticketData?.sparklines.completed}
          // sparkColor="#10b981"
          filters={{ status: 'COMPLETED' }}
        />

        <MetricCard
          title="Pending Review"
          value={ticketData?.pendingReview || 0}
          icon={<Clock className="h-5 w-5" />}
          // description="Awaiting admin review"
          filters={{ category: 'pending_review' }}
        />

        <MetricCard
          title="Rejected"
          value={ticketData?.rejected || 0}
          icon={<XCircle className="h-5 w-5" />}
          // sparkline={ticketData?.sparklines.rejected}
          // sparkColor="#ef4444"
          filters={{ status: 'REJECTED' }}
        />
      </div>

      {/* ── DETAILED STATS ──────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4 flex-1 min-h-0">
        {/* ACTIVE TICKETS BY STATUS (2 cols) */}
        <Card className="lg:col-span-2 border-border shadow-sm overflow-auto min-h-0 max-h-[800px]">
          <CardHeader>
            <CardTitle className="text-lg text-card-foreground flex items-center gap-2">
              <Ticket className="h-5 w-5 text-muted-foreground" />
              Active Tickets by Status
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-1">
              <StatusRow
                status="Pending"
                count={ticketData?.byStatus.PENDING || 0}
                total={ticketData?.totalOpen.current || 0}
                color="amber"
                filterValue={TicketStatus.PENDING}
              />
              <StatusRow
                status="En Route"
                count={ticketData?.byStatus.EN_ROUTE || 0}
                total={ticketData?.totalOpen.current || 0}
                color="blue"
                filterValue={TicketStatus.EN_ROUTE}
              />
              <StatusRow
                status="On Site"
                count={ticketData?.byStatus.ON_SITE || 0}
                total={ticketData?.totalOpen.current || 0}
                color="indigo"
                filterValue={TicketStatus.ON_SITE}
              />
              <StatusRow
                status="In Progress"
                count={ticketData?.byStatus.IN_PROGRESS || 0}
                total={ticketData?.totalOpen.current || 0}
                color="emerald"
                filterValue={TicketStatus.IN_PROGRESS}
              />
              <StatusRow
                status="On Hold"
                count={ticketData?.byStatus.ON_HOLD || 0}
                total={ticketData?.totalOpen.current || 0}
                color="rose"
                filterValue={TicketStatus.ON_HOLD}
              />
            </div>

            {/* PRIORITY DISTRIBUTION */}
            <div className="pt-4 border-t border-border">
              <h4 className="text-sm font-medium text-muted-foreground mb-4 uppercase tracking-wider">
                Priority Distribution
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <PriorityIndicator
                  label="Urgent"
                  value={ticketData?.byPriority.URGENT || 0}
                  color="rose"
                  filterValue={TicketPriority.URGENT}
                />
                <PriorityIndicator
                  label="High"
                  value={ticketData?.byPriority.HIGH || 0}
                  color="amber"
                  filterValue={TicketPriority.HIGH}
                />
                <PriorityIndicator
                  label="Medium"
                  value={ticketData?.byPriority.MEDIUM || 0}
                  color="emerald"
                  filterValue={TicketPriority.MEDIUM}
                />
                <PriorityIndicator
                  label="Low"
                  value={ticketData?.byPriority.LOW || 0}
                  color="blue"
                  filterValue={TicketPriority.LOW}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* CREW OVERVIEW (1 col) */}
        <Card className="border-border shadow-sm overflow-auto min-h-0 max-h-[800px]">
          <CardHeader>
            <CardTitle className="text-lg text-card-foreground flex items-center gap-2">
              <Users className="h-5 w-5 text-muted-foreground" />
              Crew Overview
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-8 flex-1 flex flex-col justify-center">
            {/* Available Count */}
            <div className="text-center">
              <div className="text-5xl font-bold text-primary mb-2">
                {crewData?.available || 0}
              </div>
              <div className="text-sm text-muted-foreground font-medium uppercase tracking-wide">
                Available Crews
              </div>
            </div>

            {/* Utilization Bar */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Utilization Rate</span>
                <span className="text-foreground font-bold">
                  {crewData?.utilizationRate ?? 0}%
                </span>
              </div>
              <Progress
                value={crewData?.utilizationRate ?? 0}
                className="h-2 bg-muted"
              />
            </div>

            {/* Active / Busy Grid */}
            <div
              className="p-4 bg-muted/50 rounded-lg text-center border border-border cursor-pointer hover:bg-muted transition-colors"
              onClick={() =>
                navigateTo('crew-management', { status: 'AVAILABLE' })
              }
            >
              <div className="flex justify-center mb-2">
                <UserCheck className="h-5 w-5 text-emerald-500" />
              </div>
              <div className="text-2xl font-bold text-foreground">
                {crewData?.available || 0}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                Available
              </div>
            </div>

            <div
              className="p-4 bg-muted/50 rounded-lg text-center border border-border cursor-pointer hover:bg-muted transition-colors"
              onClick={() =>
                navigateTo('crew-management', { status: 'UNAVAILABLE' })
              }
            >
              <div className="flex justify-center mb-2">
                <UserX className="h-5 w-5 text-muted-foreground" />
              </div>
              <div className="text-2xl font-bold text-foreground">
                {crewData?.unavailable || 0}
              </div>
              <div className="text-xs text-muted-foreground mt-1">Busy</div>
            </div>

            {/* Total */}
            <div className="p-4 bg-muted rounded-lg border border-border">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-muted-foreground">
                  Total Crews
                </span>
                <span className="text-xl font-bold text-foreground">
                  {crewData?.total || 0}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
